// Keeps open screens current while several people work at once.
//
//   useLiveSync()                       once, in the signed-in layout: polls the server's
//                                       change counters and announces what moved
//   useLiveRefresh(topics, refresh)     in any screen: re-runs `refresh` when one of its
//                                       topics changes (by this user, another tab or anyone)
//
// Topics are coarse on purpose (jobs, stores, production, customers, masters, users,
// finance): a screen reloads its own query, which is cheap and always correct.
import { useCallback, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { clearExpected, publish, settleServerChanges, subscribe } from "@/lib/liveBus";

const POLL_MS = 3000;
const SETTLE_MS = 250; // coalesces a burst of notices (bulk edits, autosave) into one reload

export function useLiveSync() {
  useEffect(() => {
    let known = null;
    let busy = false;

    const poll = async () => {
      if (busy || document.visibilityState !== "visible") return;
      busy = true;
      try {
        const { data } = await api.get("/live/versions");
        const now = data.versions || {};
        if (known === null) {
          clearExpected();
        } else {
          const deltas = {};
          new Set([...Object.keys(now), ...Object.keys(known)]).forEach((t) => {
            const d = (now[t] || 0) - (known[t] || 0);
            if (d) deltas[t] = d;
          });
          publish(settleServerChanges(deltas));
        }
        known = now;
      } catch {
        // Offline or server restarting: the next tick tries again.
      } finally {
        busy = false;
      }
    };

    poll();
    const timer = setInterval(poll, POLL_MS);
    // Coming back to the tab (or the network) checks at once instead of waiting for the tick.
    const wake = () => document.visibilityState === "visible" && poll();
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    window.addEventListener("online", wake);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
      window.removeEventListener("online", wake);
    };
  }, []);
}

/**
 * Re-run `refresh` whenever any of `topics` changes. Returns `reload()`, which goes
 * through the same queue: call it after the screen's own save instead of fetching
 * directly, and the save's own change notice won't cause a second fetch.
 *
 * `refresh` should update the screen quietly (no spinner, keep selection and input).
 */
export function useLiveRefresh(topics, refresh, { enabled = true } = {}) {
  const latest = useRef(refresh);
  latest.current = refresh;
  const trigger = useRef(() => latest.current());
  const key = [].concat(topics).join(",");

  useEffect(() => {
    if (!enabled) {
      trigger.current = () => latest.current();
      return undefined;
    }
    const wanted = key.split(",");
    let timer = null;
    let running = false;
    let again = false;
    let alive = true;

    const run = async () => {
      timer = null;
      if (running) {
        again = true;
        return;
      }
      running = true;
      try {
        await latest.current();
      } catch {
        // The screen keeps what it shows; the next change retries.
      } finally {
        running = false;
        if (again && alive) {
          again = false;
          schedule();
        }
      }
    };
    const schedule = () => {
      if (alive && !timer) timer = setTimeout(run, SETTLE_MS);
    };

    trigger.current = schedule;
    const off = subscribe((changed) => changed.some((t) => wanted.includes(t)) && schedule());
    return () => {
      alive = false;
      off();
      clearTimeout(timer);
    };
  }, [key, enabled]);

  return useCallback(() => trigger.current(), []);
}
